import React from 'react';
import { Link } from '@inertiajs/react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Calendar,
  Clock,
  Mail,
  User,
  Info,
  BookOpen,
  Target,
  Users,
  CheckCircle2,
  XCircle,
  FileText,
  ArrowRight,
  AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';

interface Session {
  id: number;
  session_date: string;
  duration_minutes: number;
  occurred: boolean;
  discussion_summary: string;
  mentee_todos: string | null;
  next_session_date: string | null;
}

interface MenteeDashboardProps {
  mentor: {
    id: number;
    first_name: string;
    last_name: string;
    email_address: string;
  } | null;
  match: {
    id: number;
    matched_at: string;
    status: string;
  } | null;
  sessions: Session[];
  total_hours: number;
  // null for non-mentees. Never carries the mentor's private end reason.
  pool_state: 'in_pool' | 'placed' | 'inactive' | null;
  support_email: string;
}

export default function MenteeDashboard({
  mentor,
  match,
  sessions,
  total_hours,
  pool_state,
  support_email,
}: MenteeDashboardProps) {
  // Empty state when no mentor assigned
  if (!mentor || !match) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">My Dashboard</h1>
            <p className="text-gray-600 mt-2">
              Welcome to your mentorship journey
            </p>
          </div>

          <Card className="border-0 shadow-sm">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
                <Users className="h-10 w-10 text-gray-400" />
              </div>
              <h2 className="text-2xl font-medium text-gray-900 mb-3">
                {pool_state === 'inactive'
                  ? 'Your Application Is Inactive'
                  : pool_state === 'placed'
                    ? 'Your Mentorship Is Complete'
                    : 'No Mentor Assigned Yet'}
              </h2>
              <p className="text-gray-500 text-center max-w-md mb-8">
                {pool_state === 'inactive'
                  ? "You're not currently visible to mentors browsing for new mentees, so you won't be matched until you're back in the pool."
                  : pool_state === 'placed'
                    ? 'Thank you for being part of Project SHORT. Your past sessions and notes remain available below.'
                    : "You haven't been matched with a mentor yet. Once you're matched, you'll see your mentor's information and be able to track your progress here."}
              </p>
              {pool_state === 'inactive' ? (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 max-w-md">
                  <div className="flex gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Want to be matched again?</span> Email us at{' '}
                      <a
                        href={`mailto:${support_email}`}
                        className="font-medium text-amber-800 underline"
                      >
                        {support_email}
                      </a>{' '}
                      and we'll add you back to the matching pool.
                    </p>
                  </div>
                </div>
              ) : pool_state === 'placed' ? null : (
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100 max-w-md">
                  <div className="flex gap-3">
                    <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Matching Timeline:</span> Usually happens within 1-2 weeks after your application
                      is approved. We'll notify you by email once you've been matched.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const daysSinceMatch = Math.floor(
    (new Date().getTime() - new Date(match.matched_at).getTime()) /
      (1000 * 60 * 60 * 24)
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            My Mentorship Journey
          </h1>
          <p className="text-gray-600 mt-2">
            Track your progress and manage your mentorship experience
          </p>
        </div>

        {/* Status Alert */}
        {match.status === 'active' && (
          <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100">
            <div className="flex gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700">
                <span className="font-medium">Your mentorship is active!</span> Reach out to your mentor to schedule your
                next session.
              </p>
            </div>
          </div>
        )}

        {/* Main Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Mentor Info */}
          <div className="lg:col-span-1 space-y-6">
            {/* Mentor Card */}
            <Card className="border-0 shadow-sm pt-0 pb-6">
              <CardHeader className="border-b border-gray-100 bg-gray-50/50 pt-6">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg font-medium">Your Mentor</CardTitle>
                    <CardDescription className="text-sm text-gray-500 mt-1">
                      Matched {daysSinceMatch} {daysSinceMatch === 1 ? 'day' : 'days'} ago
                    </CardDescription>
                  </div>
                  <Badge
                    variant={match.status === 'active' ? 'default' : 'secondary'}
                    className="text-xs"
                  >
                    {match.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <div className="text-lg font-medium text-gray-900">
                      {mentor.first_name} {mentor.last_name}
                    </div>
                    <div className="text-sm text-gray-500">
                      {mentor.email_address}
                    </div>
                  </div>

                  <Button className="w-full">
                    <Mail className="h-4 w-4 mr-2" />
                    Contact Mentor
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="border-0 shadow-sm pt-0 pb-6">
              <CardHeader className="border-b border-gray-100 bg-gray-50/50 pt-6">
                <CardTitle className="text-lg font-medium">Your Progress</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">Days Active</span>
                      <Calendar className="h-4 w-4 text-gray-400" />
                    </div>
                    <p className="text-3xl font-light">{daysSinceMatch}</p>
                    <div className="h-1 w-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full mt-2" />
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">Sessions</span>
                      <Clock className="h-4 w-4 text-gray-400" />
                    </div>
                    <p className="text-3xl font-light">{sessions.filter(s => s.occurred).length}</p>
                    <div className="h-1 w-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full mt-2" />
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">Hours</span>
                      <BookOpen className="h-4 w-4 text-gray-400" />
                    </div>
                    <p className="text-3xl font-light">{total_hours.toFixed(1)}</p>
                    <div className="h-1 w-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full mt-2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Activity & Resources */}
          <div className="lg:col-span-2 space-y-6">
            {/* Recent Sessions */}
            <Card className="border-0 shadow-sm pt-0 pb-6">
              <CardHeader className="border-b border-gray-100 bg-gray-50/50 pt-6">
                <div>
                  <CardTitle className="text-lg font-medium">Recent Sessions</CardTitle>
                  <CardDescription className="text-sm text-gray-500 mt-1">
                    Your mentoring session history
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
              {sessions.length > 0 ? (
                <div className="space-y-4">
                  {sessions.map((session) => (
                    <div key={session.id} className="border-l-2 border-primary pl-4 space-y-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium text-sm">
                            {format(new Date(session.session_date), 'MMMM d, yyyy')}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {session.duration_minutes} minutes • {session.occurred ? 'Completed' : 'Cancelled'}
                          </div>
                        </div>
                        {session.occurred ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600" />
                        )}
                      </div>
                      
                      {session.discussion_summary && (
                        <div className="text-sm text-muted-foreground">
                          {session.discussion_summary}
                        </div>
                      )}
                      
                      {session.mentee_todos && (
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                          <div className="flex items-start gap-2">
                            <Target className="h-4 w-4 text-blue-600 mt-0.5" />
                            <div>
                              <div className="text-sm font-medium text-blue-900">Your TODOs</div>
                              <div className="text-sm text-blue-800 whitespace-pre-wrap mt-1">
                                {session.mentee_todos}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {session.next_session_date && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          Next session: {format(new Date(session.next_session_date), 'MMM d, yyyy')}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {sessions.length >= 5 && (
                    <div className="text-center pt-4">
                      <Button variant="outline" size="sm" asChild>
                        <Link href={`/matches/${match.id}/mentorship_sessions`}>
                          <ArrowRight className="h-4 w-4 mr-2" />
                          View All Sessions
                        </Link>
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Clock
                    className="h-12 w-12 text-muted-foreground mx-auto
  mb-3"
                  />
                  <p className="text-muted-foreground">
                    No sessions logged yet
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Sessions will appear here once your mentor logs them
                  </p>
                </div>
              )}
              </CardContent>
            </Card>

            {/* Resources */}
            <Card className="border-0 shadow-sm pt-0 pb-6">
              <CardHeader className="border-b border-gray-100 bg-gray-50/50 pt-6">
                <div>
                  <CardTitle className="text-lg font-medium">Resources & Materials</CardTitle>
                  <CardDescription className="text-sm text-gray-500 mt-1">
                    Helpful resources shared by your mentor
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent className="py-12">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                    <BookOpen className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-900 font-medium mb-1">No resources shared yet</p>
                  <p className="text-sm text-gray-500">
                    Your mentor may share helpful materials here
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Tips */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
              <div className="flex gap-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-700">
                  <span className="font-medium">Pro Tip:</span> Prepare questions before each session to
                  make the most of your mentor's time. Consider keeping a journal of
                  your progress and insights from each meeting.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
